import IconVisit from "@common/components/icons/IconVisit";
import {
  Accordion,
  Anchor,
  Badge,
  Box,
  Flex,
  Loader,
  Progress,
  Title,
} from "@mantine/core";
import Link from "next/link";
import { Children, PropsWithChildren, ReactElement } from "react";
import { IAccordionHeaderProps } from "./types";
import { useStyles } from "./AccordionHeader.styled";

export default function AccordionHeader({
  "data-testid": dataTestId,
  badgeCopy,
  icon,
  title,
  titleLink,
  externalLink,
  extra,
  progress,
  loading,
  children,
  ...accordionItemProps
}: PropsWithChildren<IAccordionHeaderProps>): ReactElement {
  // children
  const cleanedUpChildren =
    Children.toArray(children).filter((child) => !!child).length > 0
      ? children
      : null;

  // styles
  const { classes } = useStyles();

  return (
    <Accordion.Item
      data-testid={dataTestId}
      value="header"
      py="xl"
      {...accordionItemProps}
    >
      <Flex
        align="flex-start"
        direction={{ base: "column", sm: "row" }}
        gap="sm"
        justify="flex-start"
        wrap="nowrap"
        maw="100%"
        w="100%"
      >
        <Accordion.Control
          className={classes.control}
          chevron={icon || <></>}
          disabled
        >
          {badgeCopy && (
            <Badge bg="yellow.3" c="gray.6" mb="sm" px="xs" tt="capitalize">
              {badgeCopy}
            </Badge>
          )}

          {titleLink ? (
            externalLink ? (
              <Anchor
                className={classes.anchor}
                href={titleLink}
                target="_blank"
                rel="noreferrer"
              >
                <Flex align="baseline" direction="row">
                  <Title m={0} mr="xs" order={2} size="h3">
                    {title}
                  </Title>
                  <IconVisit />
                </Flex>
              </Anchor>
            ) : (
              <Title m={0} order={2} size="h3">
                <Link href={titleLink}>{title}</Link>
              </Title>
            )
          ) : (
            <Title m={0} order={2} size="h3">
              {title}
            </Title>
          )}

          {loading && <Loader mt="xs" />}

          {!loading && cleanedUpChildren && (
            <Box mt="xs">{cleanedUpChildren}</Box>
          )}
        </Accordion.Control>

        {extra && <>{extra}</>}
      </Flex>

      {progress && (
        <Progress
          animate
          striped
          value={typeof progress === "number" ? progress : 100}
          mt="md"
        />
      )}
    </Accordion.Item>
  );
}
